import Modal from '../../../components/Modal';
import { useTranslation } from 'react-i18next';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import Button from '../../../components/Button';
import styled, { keyframes } from 'styled-components';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { useState } from 'react';
import {
  useBroadcastPackTransactionMutation,
  usePreparePackTransactionMutation,
} from '../../../redux/api/authorized/packs';
import { useSolana } from '../../../hooks/useSolana';
import { toast } from 'react-toastify';

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

export const PackContainer = styled.div`
  position: relative;
  display: inline-block;
  border-radius: 15px;
  overflow: hidden;
  transition: transform 0.5s ease;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};

  @media (hover: hover) {
    &:hover {
      transform: scale(1.03);
      filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.6));
      cursor: pointer;
      animation-play-state: paused;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      0deg,
      transparent,
      transparent 30%,
      rgba(255, 255, 255, 0.5)
    );
    transform: rotate(-45deg);
    transition: all 0.5s ease;
    opacity: 0;
    pointer-events: none;
  }

  @media (hover: hover) {
    &:hover::before {
      opacity: 1;
      transform: rotate(-45deg) translateY(100%);
    }
  }
`;

export const Pack = styled.img`
  width: 200px;
  display: block;
  @media ${PHONE_MEDIA_QUERY} {
    width: 90%;
    max-width: 180px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  justify-content: center;
  overflow: visible;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 5px;
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 700px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const PacksContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  gap: 30px;
`;

const LoadingContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 25px;
`;

const PackTopLoadingContainer = styled.div`
  width: 200px;
  overflow: hidden;
  display: block;
  @media ${PHONE_MEDIA_QUERY} {
    width: 90%;
    max-width: 180px;
  }
`;

const PackTopLoading = styled.img`
  width: 200px;
  display: block;

  @keyframes flapOpen {
    0% {
      transform: translateX(10%);
    }
    8.3% {
      transform: translateX(20%);
    }
    33.3% {
      transform: translateX(30%);
    }
    30% {
      transform: translateX(30%);
    }
    60% {
      transform: translateX(50%);
    }
    90% {
      transform: translateX(70%);
    }
    100% {
      transform: translateX(95%);
    }
  }

  animation: flapOpen 13s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;

  @media ${PHONE_MEDIA_QUERY} {
    width: 90%;
    max-width: 180px;
  }
`;

const PackLoading = styled.img`
  width: 200px;
  display: block;
  @media ${PHONE_MEDIA_QUERY} {
    width: 90%;
    max-width: 180px;
  }
`;

const DiversifyModal = ({ onClose, gift }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const { executePackTransaction } = useSolana();

  const handleChoosePack = async (packType) => {
    setLoading(true);
    const baseToken = gift.baseToken;
    const baseTokenAmount = gift.baseTokenAmount;

    const tokens = await executePackTransaction({
      baseToken,
      baseTokenAmount,
      packType,
    });

    if (!tokens) {
      setLoading(false);
      setSelectedPack(false);
      toast.error('Failed to open pack');
      return;
    } else {
      setLoading(false);
      dispatch(openModal({ name: 'cardReveal', props: { tokens } }));
    }
  };

  return (
    <StyledModal
      onClose={
        loading
          ? null
          : () => {
              onClose();
            }
      }
    >
      <ContentContainer>
        {loading ? (
          <LoadingContainer>
            <ParagraphLargeText>{t('diversifyModal.openingPack')}</ParagraphLargeText>
            <div>
              <PackTopLoadingContainer>
                <PackTopLoading src='/packs/top.webp' />
              </PackTopLoadingContainer>
              <PackLoading src={`/packs/opened-${selectedPack}.webp`} />
            </div>
          </LoadingContainer>
        ) : (
          <>
            <ParagraphLargeText>{t('diversifyModal.diversifyPortfolio')}</ParagraphLargeText>
            <BodyLarge>
              Diversifying means not putting all your money in one place, so if
              one investment fails, others can balance it out.
            </BodyLarge>
            <PacksContainer>
              <PackContainer
                onClick={() => {
                  setSelectedPack('stocks');
                  handleChoosePack('stocks');
                }}
                delay='0s'
              >
                <Pack src='/packs/stocks.webp' />
              </PackContainer>
              <PackContainer
                onClick={() => {
                  setSelectedPack('crypto');
                  handleChoosePack('crypto');
                }}
                delay='1.5s'
              >
                <Pack src='/packs/crypto.webp' />
              </PackContainer>
            </PacksContainer>
            <ButtonContainer>
              <Button
                variant={'white'}
                onClick={async () => {
                  onClose();
                }}
              >
                MAYBE LATER
              </Button>
            </ButtonContainer>
          </>
        )}
      </ContentContainer>
    </StyledModal>
  );
};

export default DiversifyModal;
